import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Breadcrumb, Button, Result, Spin, Table, Tabs, Tag, theme } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined, CalendarOutlined, ClockCircleOutlined, ContainerOutlined,
  EditOutlined, EnvironmentOutlined, FileDoneOutlined, FileTextOutlined, InfoCircleOutlined,
  CalculatorOutlined, TeamOutlined, CarOutlined, CheckCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";
import { useShipments } from "../hooks/useShipments";
import { useLogisticsPermissions } from "../hooks/useLogisticsPermissions";
import { Shipment } from "../types/shipment.types";
import StatusBadge from "../components/StatusBadge";
import TransportModeIcon from "../components/TransportModeIcon";
import JobCostingTab from "../components/JobCostingTab";
import InvoiceList from "../components/InvoiceList";
import SupplierBillList from "../components/SupplierBillList";
import ShipmentFormModal from "../components/ShipmentFormModal";
import ShipmentDocumentsTab from "../components/ShipmentDocumentsTab";
import { ShipmentTabHeading } from "../components/ShipmentDetailLayout";
import "./ShipmentDetail.css";

const number = (value: unknown) => {
  const result = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(result) ? result : 0;
};
const formatNumber = (value: unknown) => number(value).toLocaleString("fr-FR", { maximumFractionDigits: 0 });
const date = (value?: string, format = "DD/MM/YYYY") => value && dayjs(value).isValid()
  ? dayjs(value).format(format) : "Non renseignée";

function Field({ label, children, wide = false }: { label: string; children?: React.ReactNode; wide?: boolean }) {
  return <div className={`shipment-field${wide ? " shipment-field--wide" : ""}`}>
    <dt>{label}</dt><dd>{React.Children.toArray(children).length ? children : <span className="shipment-muted">Non renseigné</span>}</dd>
  </div>;
}

function ShipmentDetailContent({ companyId }: { companyId: unknown }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { getShipmentById } = useShipments();
  const permissions = useLogisticsPermissions();
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState("general");
  const alive = useRef(false);
  const generation = useRef(0);

  const refreshShipment = useCallback(async () => {
    const request = ++generation.current;
    if (!id || !/^[1-9]\d*$/.test(id)) {
      setError("ID du dossier manquant ou invalide"); setLoading(false); return;
    }
    const data = await getShipmentById(Number(id));
    if (!alive.current || request !== generation.current) return;
    if (data && (!companyId || String(data.company_id) === String(companyId))) {
      setShipment(data); setError(null);
    } else {
      setShipment(null); setError("Dossier introuvable ou indisponible dans cette entreprise.");
    }
    setLoading(false);
  }, [id, companyId, getShipmentById]);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    void refreshShipment();
    return () => { alive.current = false; generation.current += 1; };
  }, [refreshShipment]);

  const back = () => navigate("/logistique/dossiers");
  const style = {
    "--shipment-primary": "var(--color-primary)",
    "--shipment-text": "var(--text-primary)", "--shipment-muted": "var(--text-secondary)",
    "--shipment-border": "var(--border-color)", "--shipment-surface": "var(--bg-primary)",
    "--shipment-fill": "var(--bg-tertiary)", "--shipment-success": token.green8,
    "--shipment-success-bg": token.colorSuccessBg, "--shipment-warning": token.orange8,
    "--shipment-error": "var(--color-danger)",
  } as React.CSSProperties;

  if (loading) return <div className="shipment-detail-page shipment-loading" style={style}><Spin size="large" /><p>Chargement du dossier…</p></div>;
  if (error || !shipment) return <div className="shipment-detail-page" style={style}><Result status="404" title="Dossier introuvable" subTitle={error}
    extra={<><Button onClick={back}>Retour aux dossiers</Button><Button onClick={() => { setLoading(true); void refreshShipment(); }}>Réessayer</Button></>} /></div>;

  // Preserve the existing denominator and one-decimal margin calculation.
  const revenue = Number(shipment.total_revenue);
  const margin = Number(shipment.profit_margin);
  const safeRevenue = Number.isFinite(revenue) ? revenue : 0;
  const safeMargin = Number.isFinite(margin) ? margin : 0;
  const percentage = safeRevenue > 0 ? ((safeMargin / safeRevenue) * 100).toFixed(1) : "0.0";
  const money = (value: unknown) => `${formatNumber(value)}${shipment.currency_symbol ? ` ${shipment.currency_symbol}` : ""}`;
  const marginTone = safeMargin >= 0 ? "shipment-positive" : "shipment-negative";

  const financial = <section className="shipment-panel shipment-financial" aria-label="Synthèse financière">
    <ShipmentTabHeading title="Synthèse financière" icon={<CalculatorOutlined aria-hidden="true" />} />
    <dl className="shipment-summary-rows"><Field label="Revenus totaux">{money(shipment.total_revenue)}</Field><Field label="Coûts totaux">{money(shipment.total_cost)}</Field></dl>
    <div className="shipment-margin"><span>Marge bénéficiaire</span><strong className={marginTone}>{money(safeMargin)}</strong></div>
    <div className={`shipment-margin-rate ${marginTone}`}><span>Taux de marge</span><strong>{percentage.replace(".", ",")} %</strong></div>
    <Button block onClick={() => setActiveTabKey("costing")}>Voir le Job Costing <ArrowRightOutlined aria-hidden="true" /></Button>
  </section>;
  const transport = <section className="shipment-panel shipment-transport">
    <ShipmentTabHeading title="Transport" icon={<CarOutlined aria-hidden="true" />} />
    <dl className="shipment-summary-rows">
      <Field label="Transporteur">{shipment.carrier_name}</Field>
      <Field label="Type de service">{shipment.service_type}</Field>
      <Field label="Incoterm">{shipment.incoterm && <Tag>{shipment.incoterm}</Tag>}</Field>
      <Field label="Document de transport">{shipment.reference_client}</Field>
      {shipment.vessel_name && <Field label="Navire / vol">{shipment.vessel_name}</Field>}
      {shipment.voyage_number && <Field label="N° de voyage">{shipment.voyage_number}</Field>}
      {shipment.clearing_agent_name && <Field label="Agent de dédouanement">{shipment.clearing_agent_name}</Field>}
    </dl>
  </section>;

  const general = <div className="shipment-detail-grid">
    <div className="shipment-panel shipment-main">
      <section className="shipment-section">
        <ShipmentTabHeading title="Itinéraire" icon={<EnvironmentOutlined aria-hidden="true" />} />
        <div className="shipment-route">
          <div><strong>{shipment.origin_city || "Origine non renseignée"}</strong><span>{shipment.origin_country || "Pays non renseigné"}</span></div>
          <div className="shipment-route-line" aria-hidden="true"><TransportModeIcon mode={shipment.transport_mode} showLabel={false} /><ArrowRightOutlined aria-hidden="true" /></div>
          <div><strong>{shipment.destination_city || "Destination non renseignée"}</strong><span>{shipment.destination_country || "Pays non renseigné"}</span></div>
        </div>
        <dl className="shipment-field-grid">
          <Field label="Adresse d’origine">{shipment.origin_address}</Field><Field label="Adresse de destination">{shipment.destination_address}</Field>
          {shipment.port_of_loading && <Field label="Port de chargement">{shipment.port_of_loading}</Field>}
          {shipment.port_of_discharge && <Field label="Port de déchargement">{shipment.port_of_discharge}</Field>}
          {shipment.final_destination && <Field label="Destination finale" wide>{shipment.final_destination}</Field>}
        </dl>
      </section>
      <section className="shipment-section">
        <ShipmentTabHeading title="Intervenants" icon={<TeamOutlined aria-hidden="true" />} />
        <dl className="shipment-field-grid">
          <Field label="Client">{shipment.client_name}</Field><Field label="Expéditeur">{shipment.shipper_name}</Field>
          <Field label="Consignataire">{shipment.consignee_name}</Field>
          <Field label="Destinataire final">{shipment.receiver_name}{shipment.receiver_address && <small>{shipment.receiver_address}</small>}</Field>
        </dl>
      </section>
      <section className="shipment-section">
        <ShipmentTabHeading title="Marchandise" icon={<ContainerOutlined aria-hidden="true" />} />
        {shipment.goods_description && <p className="shipment-goods-description">{shipment.goods_description}</p>}
        <dl className="shipment-goods-metrics">
          <Field label="Poids total">{shipment.total_weight ? `${shipment.total_weight} kg` : "—"}</Field>
          <Field label="Volume total">{shipment.total_volume ? `${shipment.total_volume} m³` : "—"}</Field>
          <Field label="Nombre de colis">{shipment.number_of_packages || "—"}</Field>
        </dl>
        {(shipment.bl_number || shipment.container_numbers) && <dl className="shipment-field-grid">
          {shipment.bl_number && <Field label="N° BL / AWB">{shipment.bl_number}</Field>}
          {shipment.container_numbers && <Field label="N° conteneurs">{shipment.container_numbers}</Field>}
        </dl>}
      </section>
    </div>
    <aside className="shipment-sidebar">{financial}{transport}</aside>
  </div>;

  const dates = <div className="shipment-detail-grid">
    <div className="shipment-panel shipment-main">
      <ShipmentTabHeading title="Dates du dossier" icon={<CalendarOutlined aria-hidden="true" />} />
      <section className="shipment-section"><h3>Calendrier de transport</h3>
        <ol className="shipment-milestones">
          {[{ label: "Réservation", value: shipment.booking_date, place: undefined }, { label: "Départ estimé · ETD", value: shipment.etd, place: shipment.origin_city },
            { label: "Arrivée estimée · ETA", value: shipment.eta, place: shipment.destination_city }].map((step, index) =>
            <li key={step.label}>{index === 0 && step.value ? <CheckCircleOutlined aria-hidden="true" /> : <ClockCircleOutlined aria-hidden="true" />}<span>{step.label}</span>
              <strong>{date(step.value)}</strong><small>{index === 0 ? (step.value ? "Renseignée" : "Non renseignée") : "Prévisionnel"}</small>{step.place && <small>{step.place}</small>}</li>)}
        </ol>
      </section>
      <section className="shipment-section"><h3>Prévisionnel et réalisé</h3>
        <Table size="middle" pagination={false} rowKey="step" tableLayout="fixed" dataSource={[
          { step: "Départ", estimated: date(shipment.etd), actual: date(shipment.actual_departure_date) },
          { step: "Arrivée", estimated: date(shipment.eta), actual: date(shipment.actual_arrival_date) },
        ]} columns={[{ title: "Étape", dataIndex: "step" }, { title: "Date estimée", dataIndex: "estimated" }, { title: "Date réelle", dataIndex: "actual" }]} />
      </section>
      <section className="shipment-section"><ShipmentTabHeading title="Suivi du dossier" icon={<ClockCircleOutlined aria-hidden="true" />} />
        <dl className="shipment-field-grid"><Field label="Créé le">{date(shipment.created_at, "DD/MM/YYYY [à] HH:mm")}</Field>
          {shipment.updated_at && <Field label="Dernière modification">{date(shipment.updated_at, "DD/MM/YYYY [à] HH:mm")}</Field>}</dl>
        {shipment.internal_notes && <Alert className="shipment-internal-notes" type="info" showIcon message="Notes internes" description={<span className="shipment-preserve-lines">{shipment.internal_notes}</span>} />}
      </section>
    </div><aside className="shipment-sidebar">{financial}{transport}</aside>
  </div>;

  return <div className="shipment-detail-page" style={style}>
    <div className="shipment-page-navigation"><Button type="text" icon={<ArrowLeftOutlined aria-hidden="true" />} onClick={back}>Retour aux dossiers</Button>
      <Breadcrumb items={[{ title: "Logistique" }, { title: "Dossiers" }, { title: shipment.shipment_number }]} /></div>
    <header className="shipment-page-header"><div className="shipment-identity"><FileTextOutlined className="shipment-identity-icon" />
      <div><div className="shipment-title-row"><h1>{shipment.shipment_number}</h1><StatusBadge status={shipment.status} color={shipment.status === "confirmed" ? "success" : undefined} /><TransportModeIcon mode={shipment.transport_mode} /></div><p>{shipment.client_name}</p></div></div>
      {permissions.canCreateShipments && <Button size="large" type={activeTabKey === "general" || activeTabKey === "dates" ? "primary" : "default"} icon={<EditOutlined aria-hidden="true" />} onClick={() => setEditModalVisible(true)}>Modifier le dossier</Button>}
    </header>
    <Tabs className="shipment-detail-tabs" activeKey={activeTabKey} onChange={setActiveTabKey} items={[
      { key: "general", label: "Informations générales", icon: <InfoCircleOutlined aria-hidden="true" />, children: general },
      { key: "dates", label: "Dates", icon: <CalendarOutlined aria-hidden="true" />, children: dates },
      { key: "documents", label: "Documents", icon: <FileDoneOutlined aria-hidden="true" />, children: <ShipmentDocumentsTab shipmentId={shipment.id} attachments={shipment.attachments} canEdit={permissions.canUpdateShipmentStatus || permissions.canCreateShipments} /> },
      { key: "costing", label: "Job Costing", icon: <CalculatorOutlined aria-hidden="true" />, children: <JobCostingTab shipmentId={shipment.id} currencySymbol={shipment.currency_symbol} onRefreshNeeded={refreshShipment} /> },
      { key: "client-invoices", label: "Factures clients", icon: <FileTextOutlined aria-hidden="true" />, children: <InvoiceList shipmentId={shipment.id} onRefreshNeeded={refreshShipment} onOpenCosting={() => setActiveTabKey("costing")} /> },
      { key: "supplier-bills", label: "Factures fournisseurs", icon: <FileTextOutlined aria-hidden="true" />, children: <SupplierBillList shipmentId={shipment.id} onRefreshNeeded={refreshShipment} onOpenCosting={() => setActiveTabKey("costing")} /> },
    ]} />
    {permissions.canCreateShipments && <ShipmentFormModal visible={editModalVisible} onClose={() => { setEditModalVisible(false); void refreshShipment(); }} shipment={shipment} mode="edit" />}
  </div>;
}

// Changing account/company/establishment discards the old screen and its open dialogs.
export default function ShipmentDetail() {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user } = useAuth();
  const { id } = useParams();
  return <ShipmentDetailContent key={`${user?.id || ""}:${selectedCompany || ""}:${selectedWarehouse || ""}:${id}`} companyId={selectedCompany} />;
}
